import React, { useState, useCallback, useEffect } from 'react';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import '../Filters/Filters.scss';

const SliderFilter = ({
  label,
  value,
  onChange,
  min = 1,
  max = 10,
  step = 1,
  isRange = false,
  onBlur
}) => {
  const [hovered, setHovered] = useState(false);

  const getRangeValues = useCallback(() => {
    if (isRange) {
      return Array.isArray(value) ? value : [min, value || max];
    }
    return value !== undefined ? value : min;
  }, [value, isRange, min, max]);

  const sliderValue = getRangeValues();

  // Local state for inputs to allow intermediate invalid typing (like empty string)
  const [localMin, setLocalMin] = useState('');
  const [localMax, setLocalMax] = useState('');
  const [localSingle, setLocalSingle] = useState('');

  // Sync local inputs when slider value changes externally (e.g., from parent or dragging)
  useEffect(() => {
    if (isRange) {
      setLocalMin(sliderValue[0]);
      setLocalMax(sliderValue[1]);
    } else {
      setLocalSingle(sliderValue);
    }
  }, [sliderValue, isRange]);

  const handleChange = (newValue) => {
    onChange(newValue);
  };

  const handleInputBlur = () => {
    if (isRange) {
      let newMin = parseFloat(localMin);
      let newMax = parseFloat(localMax);

      if (isNaN(newMin)) newMin = min;
      if (isNaN(newMax)) newMax = max;

      // Fix floating point precision issues
      newMin = parseFloat(newMin.toFixed(10));
      newMax = parseFloat(newMax.toFixed(10));

      // Ensure values are within min and max boundaries
      newMin = Math.max(min, Math.min(newMin, max));
      newMax = Math.max(min, Math.min(newMax, max));

      // Ensure min is not greater than max and vice versa
      if (newMin > newMax) {
        newMin = newMax;
      }

      // Enforce step constraints
      if (step) {
        newMin = Math.round((newMin - min) / step) * step + min;
        newMax = Math.round((newMax - min) / step) * step + min;
      }

      // Fix floating point precision issues after step calculations
      newMin = parseFloat(newMin.toFixed(10));
      newMax = parseFloat(newMax.toFixed(10));

      setLocalMin(newMin);
      setLocalMax(newMax);

      // Only fire change if it actually differs (optional, but good practice)
      if (newMin !== sliderValue[0] || newMax !== sliderValue[1]) {
        handleChange([newMin, newMax]);
      }
    } else {
      let newSingle = parseFloat(localSingle);
      if (isNaN(newSingle)) newSingle = min;

      // Fix floating point precision issues
      newSingle = parseFloat(newSingle.toFixed(10));

      newSingle = Math.max(min, Math.min(newSingle, max));

      if (step) {
        newSingle = Math.round((newSingle - min) / step) * step + min;
      }

      // Fix floating point precision issues after step calculations
      newSingle = parseFloat(newSingle.toFixed(10));

      setLocalSingle(newSingle);

      if (newSingle !== sliderValue) {
        handleChange(newSingle);
      }
    }

    // Call onBlur prop if provided
    onBlur?.();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleInputBlur();
    }
  };

  const formatValue = (val) => {
    return typeof val === 'number' ? val.toFixed(step < 1 ? 2 : 0) : val;
  };

  const handleRender = (node, props) => {
    return React.cloneElement(node, {
      children: (
        <React.Fragment>
          {(props.dragging || hovered) && (
            <div
              className="slider-tooltip"
              style={{
                display: 'block',
                position: 'absolute',
                top: '-35px',
                left: '50%',
                transform: 'translateX(-50%)',
              }}
            >
              {formatValue(props.value)}
            </div>
          )}
        </React.Fragment>
      )
    });
  };

  return (
    <div className="filter-control">
      <label className="filter-label">{label}</label>
      <div
        className="slider-container"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <div className="slider-wrapper">
          <Slider
            range={isRange}
            min={min}
            max={max}
            step={step}
            value={sliderValue}
            onChange={handleChange}
            handleRender={handleRender}
            onBlur={() => onBlur?.()}
            styles={{
              track: { backgroundColor: '#9f173e', height: 6 },
              handle: {
                borderColor: '#ffffff',
                backgroundColor: '#f7921a',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                borderWidth: 2,
                borderStyle: 'solid',
                width: 18,
                height: 18,
                marginTop: -6,
                opacity: 1
              },
              rail: { backgroundColor: '#e5e7eb', height: 6 }
            }}
          />
          <div className="slider-labels">
            <span>{min}</span>
            <span>{max}</span>
          </div>

        </div>

        {/* Number Inputs to set value directly */}
        <div className="slider-inputs" style={{ display: 'flex', gap: '8px', alignItems: 'center', marginLeft: '12px' }}>
          {isRange ? (
            <>
              <input
                type="number"
                className="filter-input"
                style={{ width: '65px', padding: '4px 8px', height: '32px' }}
                value={localMin}
                min={min}
                max={Math.max(localMax || min, min)}
                step={step}
                onChange={(e) => setLocalMin(e.target.value)}
                onBlur={handleInputBlur}
                onKeyDown={handleKeyDown}
              />
              <span style={{ color: '#6b7280', fontWeight: 500 }}>-</span>
              <input
                type="number"
                className="filter-input"
                style={{ width: '65px', padding: '4px 8px', height: '32px' }}
                value={localMax}
                min={Math.min(localMin || max, max)}
                max={max}
                step={step}
                onChange={(e) => setLocalMax(e.target.value)}
                onBlur={handleInputBlur}
                onKeyDown={handleKeyDown}
              />
            </>
          ) : (
            <input
              type="number"
              className="filter-input"
              style={{ width: '65px', padding: '4px 8px', height: '32px' }}
              value={localSingle}
              min={min}
              max={max}
              step={step}
              onChange={(e) => setLocalSingle(e.target.value)}
              onBlur={handleInputBlur}
              onKeyDown={handleKeyDown}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SliderFilter;
